import { clsx } from "clsx";
import { type CSSProperties, forwardRef, type PropsWithChildren, useEffect, useRef, useState } from "react";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import { useResizeObserver } from "../../hooks/useResizeObserver.ts";
import { ActionButton } from "../ActionButton/ActionButton.tsx";
import { ActionButtonIntent, ActionButtonSize } from "../ActionButton/settings.ts";
import type { GridCard } from "../CardGrid/CardGrid.tsx";
import { HeaderBar, type HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { LoadingBar } from "../LoadingBar/LoadingBar.js";
import style from "./Card.module.css";
import { CardRowSkeleton } from "./CardRow.tsx";

export interface CardProps extends Omit<HeaderBarProps, "className" | "style" | "domTitleProps">, GridCard {
  className?: string;
  style?: CSSProperties;
  headerClassName?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  /** TODO: Should we switch to pagination? */
  shrinkable?: boolean;
  scrollable?: boolean;
  noPadding?: boolean;
  noBorder?: boolean;
  fillHeight?: boolean;
  updating?: boolean;
  isLoading?: boolean;
}

const INFO_CARD_SHRINK_MIN = 400;

export const Card = forwardRef<HTMLDivElement, PropsWithChildren<CardProps>>(function Card(props, ref) {
  const domRef = useDOMRef<HTMLDivElement>(ref);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isShrank, setShrink] = useState(props.shrinkable);
  const [isShrinkable, setShrinkable] = useState(props.shrinkable);
  const contentBoxSize = useResizeObserver(contentRef);

  useEffect(() => {
    const shrinkable = props.shrinkable && (contentBoxSize?.height ?? 0) > INFO_CARD_SHRINK_MIN;
    setShrinkable(shrinkable);
  }, [contentBoxSize, props.shrinkable]);

  const dataAttributes = useDataAttributes(props);
  const testId = `${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}card`;

  return (
    <div
      ref={domRef}
      className={clsx(style.card, props.className, {
        [style.card_fill]: props.fillHeight,
        [style.card_scrollable]: props.scrollable,
        [style.card_shrinkable]: props.shrinkable,
        [style.no_style]: props.noBorder,
      })}
      {...dataAttributes}
      data-testid={testId}
      {...{ "data-e2e-shrink": isShrinkable ? String(isShrank) : undefined }}
      style={props.style}
    >
      {(!!props.title || !!props.actions) && (
        <HeaderBar
          title={props.title}
          subtitle={props.subtitle}
          tag={props.tag}
          actions={props.actions}
          icon={props.icon ? <Icon icon={props.icon} /> : undefined}
          className={clsx(style.header_bar, props.headerClassName)}
        />
      )}
      {!!props.updating && <LoadingBar isLoading style={{ position: "relative", top: 0, left: 0, width: "100%" }} />}
      <div className={style.body} style={{ maxHeight: isShrank ? INFO_CARD_SHRINK_MIN : undefined }}>
        <div
          ref={contentRef}
          className={clsx(style.body_content, props.contentClassName)}
          style={{
            height: isShrank ? "unset" : undefined,
            padding: props.noPadding === true ? 0 : undefined,
            ...props.contentStyle,
          }}
          data-testid={"card-content"}
        >
          {props.isLoading ? (
            <>
              <CardRowSkeleton />
              <CardRowSkeleton />
              <CardRowSkeleton />
            </>
          ) : (
            props.children
          )}
        </div>
      </div>
      {!!isShrinkable && (
        <div className={clsx(style.footer, { [style.footer_shadow]: isShrank })} data-testid={"card-shrink"}>
          <ActionButton
            onPress={() => {
              setShrink(!isShrank);
            }}
            label={`${isShrank ? "Expand" : "Shrink"} the Card`}
            intent={ActionButtonIntent.Secondary}
            size={ActionButtonSize.InTableCell}
          />
        </div>
      )}
    </div>
  );
});
